import MineDamageLogic from "../../logic/MineDamageLogic";
import { Settings } from "../../Settings";
import Character from "../character/Character";
import { CharacterAction } from "../character/CharacterAction";
import PartyCharacter from "../character/PartyCharacter";
import PartyTarget from "../character/PartyTarget";
import MineTurnAction from "../character/turn/MineTurnAction";
import PartyMoveTurnAction from "../character/turn/PartyMoveTurnAction";
import TurnAction from "../character/turn/TurnAction";
import GridTile from "../grid/GridTile";
import VectorField from "../grid/VectorField";
import Vector from "../math/Vector";
import PathPlanFinder from "../path/PathPlanFinder";
import { VectorFieldScores } from "../path/VectorFieldScores";
import StatisticsIncrementor from "../statistics/StatisticsIncrementor";
import Crew from "./Crew";

export default class CrewBrain {
    /**
     * @param {Crew} crew
     * @param {VectorField} vectorField 
     * @param {PartyTarget} partyTarget
     * @param {PathPlanFinder} pathPlanFinder
     * @param {TileMinedLogic} tileMinedLogic
     * @param {MineDamageLogic} mineDamageLogic
     * @param {StatisticsIncrementor} statisticsIncrementor
     */
    constructor(crew, vectorField, partyTarget, pathPlanFinder, tileMinedLogic, mineDamageLogic, statisticsIncrementor) {
        /**
         * @constant
         * @type {Crew} 
         */
        this.crew = crew;
        /**
         * @constant
         * @type {VectorField}
         */
        this.vectorField = vectorField;
        /**
         * @constant
         * @type {PartyTarget}
         */
        this.partyTarget = partyTarget;
        /**
         * @constant
         * @type {PathPlanFinder}
         */
        this.pathPlanFinder = pathPlanFinder;
        /**
         * @constant
         * @type {TurnAction}
         */
        this.mineTurnAction = new MineTurnAction(tileMinedLogic);
        /**
         * @constant
         * @type {TurnAction}
         */
        this.moveTurnAction = new PartyMoveTurnAction(statisticsIncrementor, mineDamageLogic, tileMinedLogic, vectorField);

        /**
         * @constant
         * @type {PartyCharacter[]}
         */
        this.unassignedMiners = [];

        /**
         * @type {number}
         */
        this.unassignedMinerCount = 0;

        /**
         * @constant
         * @type {GridTile[]}
         */
        this.claimedTiles = [];

        /**
         * @constant
         * @type {GridTile[]}
         */
        this.bestTiles = [];

        /**
         * @constant
         * @type {Vector}
         */
        this.workingPosition = new Vector(0, 0);

        /**
         * @constant
         * @type {number}
         */
        this.nearDistance2 = (5 * Settings.tile.size) * (5 * Settings.tile.size); // 
    }

    updateForTurn() {

        if (this.vectorField.ignoreCommonTiles && this.vectorField.ignorePremiumTiles) {
            this.assignTurnsForDestinationTiles();
        }
        else {
            if (this.vectorField.isVectorFieldMineTargetsEmpty()) {
                this.assignTurnsForDestinationTiles();
            }
            else {
                this.assignTurnsForVectorField();
            }    
        }

        /////////////////////////////////////
        // Set the turn actions
        /////////////////////////////////////

        this.setTurnActions();
    }

    /**
     * @private
     */
    assignTurnsForDestinationTiles() {
        const miners = this.crew.miners;
        const destinationTiles = this.vectorField.destinationTileCandidates;

        ///////////////////////////////////////
        // Evaluate the miner targets, populate unassigned miners
        ///////////////////////////////////////

        for (let i = 0; i < miners.length; i++) {
            const miner = miners[i];

            const closestDestinationTile = this.findClosestTile(miner.position.worldCoordinateOrigin, destinationTiles);

            if (closestDestinationTile) {
                // Returns true if the current target & path is OK
                if (this.evaluateMinerDestination(miner, closestDestinationTile)) {
                    continue;
                }

                this.assignMinerTarget(miner, closestDestinationTile, null);
                continue;
            }

            miner.resetTurnState();
        }
    }

    /**
     * @private
     */
    assignTurnsForVectorField() {
        const miners = this.crew.miners;

        const priorityTiles = this.vectorField.priorityMiningCandidates;   // #1 priority
        const currencyTiles = this.vectorField.currencyMiningCandidates;   // #2 priority
        const commonTiles = this.vectorField.commonMiningCandidates;       // #3

        ///////////////////////////////////////
        // Initialize unassigned miners
        ///////////////////////////////////////

        this.claimedTiles.length = 0;

        // Clear out the array
        this.unassignedMiners.fill(null);
        this.unassignedMinerCount = 0;

        ///////////////////////////////////////
        // Evaluate the miner targets, populate unassigned miners
        ///////////////////////////////////////

        for (let i = 0; i < miners.length; i++) {
            this.evaluateMinerTarget(miners[i]);
        }

        for (let i = 0; i < this.unassignedMiners.length; i++) {
            const miner = this.unassignedMiners[i];
            if (!miner) {
                continue;
            }

            if (priorityTiles.length > 0 && this.assignMinerToClosestAvailableTile(miner, i, priorityTiles)) {
                continue;
            }
            if (currencyTiles.length > 0 && this.assignMinerToClosestAvailableTile(miner, i, currencyTiles)) {
                continue;
            }
            if (commonTiles.length > 0 && this.assignMinerToClosestAvailableTile(miner, i, commonTiles)) {
                continue;
            }
        }

        ///////////////////////////////////////
        // Unassigned miners: Help somebody out?
        ///////////////////////////////////////

        if (this.unassignedMinerCount > 0) {
            for (let i = this.unassignedMiners.length - 1; i >= 0; i--) {
                const miner = this.unassignedMiners[i];
                if (!miner) {
                    continue;
                }

                this.assignMinerToHelpOthers(miner, i);
            }
        }
        
        ///////////////////////////////////////
        // Leftovers: stand still
        ///////////////////////////////////////

        if (this.unassignedMinerCount > 0) {
            for (let i = 0; i < this.unassignedMiners.length; i++) {
                const miner = this.unassignedMiners[i];
                if (!miner) {
                    continue;
                }

                ////////console.log('crew brain - miner is unassigned - reset turn state');

                miner.resetTurnState();
                miner.setCharacterAction(CharacterAction.STAND);
            }
        }
    }

    /**
     * @private
     * @param {Character} miner 
     * @param {number} minerIndex
     */
    assignMinerToHelpOthers(miner, minerIndex) {

        // 1. Find a nearby tile that is being targeted
        const mineTile = this.findClosestTargetedMineTile(miner.position.tile.origin);
        if (!mineTile) {
            return;
        }
        
        // 2. Choose a good neighbor tile.
        const neighborTile = this.findBestNeighborTile(mineTile, miner.position.tile);

        if (!neighborTile) {
            console.log('CrewBrain.assignMinerToHelpOthers() failed to find miner for tile.');
            return;
        }

        // Clear out the miner from the unassigned miners array.
        this.unassignedMiners[minerIndex] = null;
        this.unassignedMinerCount--;

        this.assignMinerTarget(miner, neighborTile, mineTile);
    }


    /**
     * @private
     * @param {Character} miner 
     * @param {number} unassignedMinerIndex
     * @param {GridTile[]} tiles 
     * @returns {boolean}
     */
    assignMinerToClosestAvailableTile(miner, unassignedMinerIndex, tiles) {
        const tile = this.findClosestUnclaimedTile(miner.position.worldCoordinateOrigin, tiles);
        if (!tile) {
            return false;
        }

        if (!this.claimTargetForMiner(miner, tile)) {
            return false;
        }

        // The miner is now assigned.
        this.unassignedMiners[unassignedMinerIndex] = null;
        this.unassignedMinerCount--;

        return true;
    }

    /**
     * @private
     * @param {Character} miner 
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    claimTargetForMiner(miner, tile) {

        // Choose a good neighbor tile for them to walk to
        const neighborTile = this.findBestNeighborTile(tile, miner.position.tile);
        if (!neighborTile) {
            console.log('CrewBrain.assignTargetForMiner() failed to find miner for tile.');
            return false;
        }

        this.assignMinerTarget(miner, neighborTile, tile);

        this.claimedTiles.push(tile);

        return true;
    }

    /**
     * @private
     * @param {Character} miner 
     */
    evaluateMinerTarget(miner) {
        if (!miner.position.tile) {
            return;
        }

        // Make sure the mine target is valid
        const mineTile = miner.target.mineTile;
        if (mineTile) {
            if (this.isCurrentMineTargetValid(mineTile)) {
                // The mine target is fine, so we are done.
                this.claimedTiles.push(miner.target.mineTile);
                return;
            }
        }

        // Add the miner to the array of unassigned miners.
        this.unassignedMiners[this.unassignedMinerCount] = miner;
        this.unassignedMinerCount++;
    }

    /**
     * @private
     * @param {Character} miner 
     * @param {GridTile} closestDestinationTile
     * @returns {boolean} True if the miner has a good path to the target. False if they need to be assigned.
     */
    evaluateMinerDestination(miner, closestDestinationTile) {
        if (!miner.position.tile) {
            return true; // Do not return false because they are messed up.
        }

        const targetTile = miner.target.targetTile;
        if (targetTile && miner.position.pathPlan.length > 0) {
            const dist2 = targetTile.origin.distanceSquared(closestDestinationTile.origin);
            if (dist2 <= this.nearDistance2) {
                return true; // their path is OK
            }
        }

        return false;
    }

    /**
     * @private
     * @param {GridTile} mineTile
     * @returns {boolean}
     */
    isCurrentMineTargetValid(mineTile) {
        // If the miner has a valid mine tile, then maybe we just keep it?            
        if (!mineTile || mineTile.open) {
            return false;
        }

        const priorityTiles = this.vectorField.priorityMiningCandidates;   // #1 priority
        const currencyTiles = this.vectorField.currencyMiningCandidates;   // #2 priority

        // If our target is common and there are better targets available, then the target is invalid.
        if (mineTile.isCommon() && (priorityTiles.length > 0 || currencyTiles.length > 0)) {
            return false;
        }
        
        // Tiles with mineScore = 0 are worthless.
        if (mineTile.pathData.getMineScore(this.vectorField.vectorFieldNumber) <= VectorFieldScores.WORTHLESS) {
            return false;
        }

        if (mineTile.isPriority() && priorityTiles.length > 0 && priorityTiles.indexOf(mineTile) !== -1) {
            return true;
        }

        if (mineTile.isCurrency() && currencyTiles.length > 0 && currencyTiles.indexOf(mineTile) !== -1) {
            return true;
        }

        const commonTiles = this.vectorField.commonMiningCandidates;       // #3
        if (mineTile.isCommon() && commonTiles.length > 0 && commonTiles.indexOf(mineTile) !== -1) {
            return true;
        }

        return false;
    }


    /**
     * @private
     * @param {Vector} origin
     * @returns {GridTile}
     */
    findClosestTargetedMineTile(origin) {
        const miners = this.crew.miners;

        let closestTile = null;
        let closestDist2 = 1000000;

        for (let i = 0; i < miners.length; i++) {
            const tile = miners[i].target.mineTile;
            if (!tile) {
                continue;
            }

            const dist2 = origin.distanceSquared(tile.origin);

            if (!closestTile || closestDist2 > dist2) {
                closestTile = tile;
                closestDist2 = dist2;
            }
        }

        return closestTile;
    }

    /**
     * @private
     * @param {Vector} origin 
     * @param {GridTile[]} tiles 
     * @returns {GridTile}
     */
    findClosestUnclaimedTile(origin, tiles) {
        let closestTile = null;
        let closestDist2 = 1000000;

        for (let i = 0; i < tiles.length; i++) {
            const tile = tiles[i];

            const dist2 = origin.distanceSquared(tile.origin);

            if (!closestTile || closestDist2 > dist2) {
                // Check to be sure the tile is unclaimed
                if (this.claimedTiles.indexOf(tile) !== -1) {
                    continue;
                }

                closestTile = tile;
                closestDist2 = dist2;
            }
        }

        return closestTile;
    }

    /**
     * @private
     * @param {Vector} origin 
     * @param {GridTile[]} tiles 
     * @returns {GridTile}
     */
    findClosestTile(origin, tiles) {
        let closestTile = null;
        let closestDist2 = 1000000;

        for (let i = 0; i < tiles.length; i++) {
            const tile = tiles[i];
            if (!tile) {
                continue;
            }

            const dist2 = origin.distanceSquared(tile.origin);

            if (!closestTile || closestDist2 > dist2) {
                closestTile = tile;
                closestDist2 = dist2;
            }
        }

        return closestTile;
    }

    /**
     * @private
     * @param {Vector} origin 
     * @param {GridTile[]} tiles 
     * @returns {GridTile}
     */
    findClosestOpenTile(origin, tiles) {
        let closestTile = null;
        let closestDist2 = 1000000;

        for (let i = 0; i < tiles.length; i++) {
            const tile = tiles[i];
            if (!tile || !tile.open) {
                continue;
            }

            const dist2 = origin.distanceSquared(tile.origin);

            if (!closestTile || closestDist2 > dist2) {
                closestTile = tile;
                closestDist2 = dist2;
            }
        }

        return closestTile;
    }

    /**
     * @private
     */
    setTurnActions() {
        const miners = this.crew.miners;
        for (let i = 0; i < miners.length; i++) {
            this.setMinerTurnAction(miners[i]);
        }
    }

    /**
     * @private
     * @param {PartyCharacter} miner 
     */
    setMinerTurnAction(miner) {
        const mineTile = miner.target.mineTile;

        if (miner.position.pathPlan.length > 0) {
            miner.turnAction = this.moveTurnAction;
        }
        else if (mineTile && !mineTile.open && miner.position.tile.isNeighborTile(mineTile)) {
            miner.turnAction = this.mineTurnAction;
        }
        else {
            miner.resetTurnState();
        }
    }

    /**
     * @private
     * @param {GridTile} targetTile 
     * @param {GridTile} minerTile 
     * @returns {GridTile}
     */
    findBestNeighborTile(targetTile, minerTile) {
        // If the miner tiles is a neighbor tile, then we are done.
        if (targetTile.isNeighborTile(minerTile)) {
            return minerTile;
        }

        // use relative positions of the two tiles.

        const neighbors = targetTile.getNeighbors();
        const closestOpenNeighbor = this.findClosestOpenTile(minerTile.origin, neighbors);
        if (closestOpenNeighbor && closestOpenNeighbor.open) {
            return closestOpenNeighbor;
        }

        // Find the first open neighbor that is also a neighbor of the miner tile.
        for (let i = 0; i < neighbors.length; i++) {
            const neighbor = neighbors[i];
            if (!neighbor || !neighbor.open) {
                continue;
            }

            if (neighbor.isNeighborTile(minerTile)) {
                return neighbor;
            }
        }

        // Just pick the closest neighbor tile to the miner.
        let closest = null;
        let closestDist2 = 1000000;

        for (let i = 0; i < neighbors.length; i++) {
            const neighbor = neighbors[i];
            if (!neighbor) {
                continue;
            }

            const dist2 = neighbor.origin.distanceSquared(minerTile.origin);
            if (!closest || closestDist2 > dist2) {
                closest = neighbor;
                closestDist2 = dist2;
            }
        }

        return closest;
    }

    /**
     * @private
     * @param {PartyCharacter} miner 
     * @param {GridTile} destinationTile
     * @param {GridTile} mineTile
     */
    assignMinerTarget(miner, destinationTile, mineTile) { 
        // This is our new target
        miner.target.setTargetTile(destinationTile, mineTile, 'crewBrain');

        if (destinationTile !== miner.position.tile) {

            let startAtStart = true;
            if (!miner.position.isCenteredOnTile()) {
                if (miner.position.isNearTileOrigin()) {
                    // We want to assign the miner the task of walking to the center of the current tile.
                    miner.position.setPositionToTileOrigin();
                    miner.resetTurnState();
                }
                else {
                    // This is ok, but we can't start the new path from the miner position.
                    startAtStart = false;
                }
            }

            this.pathPlanFinder.findPartyCharacterPathPlan(miner.position.tile, destinationTile, miner.position.pathPlan, startAtStart);
        }
    }

}